"use client"

import { useState, useEffect } from "react"
import { sdk } from "@farcaster/miniapp-sdk"
import { Header } from "./components/Header"
import { AssetSelector } from "./components/AssetSelector"
import { ActionTabs } from "./components/ActionTabs"
import { Dashboard } from "./components/Dashboard"
import { SocialModal } from "./components/SocialModal"
import { SecurityDisclaimer } from "./components/SecurityDisclaimer"
import { AAVE_V3_BASE_TOKENS } from "./data/tokens"
import type { Token } from "./types"
import { WalletProvider } from "./contexts/WalletContext"
import { assertNoExtensionProvider } from "./lib/antiExtensionTripwire"
import { probeEmbeddedProvider } from "./lib/probeEmbeddedProvider"
import { useAaveData, useEnrichedTokens } from "./hooks/useAaveData"
import { isolateFarcasterWallet, ensureFarcasterProvider, preventWalletConflicts } from "./lib/walletIsolation"

function App() {
  const [showSocialModal, setShowSocialModal] = useState(false)
  const [socialModalData, setSocialModalData] = useState<{
    action: string
    amount: string
    token: string
  } | null>(null)
  // Fetch Aave data once at the app level
  const { data: aaveData, isLoading: aaveLoading } = useAaveData()
  const { tokens: enrichedTokens } = useEnrichedTokens()
  
  const [selectedToken, setSelectedToken] = useState<Token>(enrichedTokens[0] || AAVE_V3_BASE_TOKENS[0])

  // Build marker
  console.log(
    '[build]',
    (import.meta as any)?.env?.VITE_GIT_COMMIT || 'unknown-commit',
    new Date().toISOString()
  )

  // Diagnostic logs
  console.log("[diag] VITE_DISABLE_EVM_SCAN:", (import.meta as any).env?.VITE_DISABLE_EVM_SCAN);
  console.log("[diag] location.host:", window.location.host);

  // Call ready() when the interface is ready to be displayed
  // This follows the official Farcaster getting started guide
  useEffect(() => {
    (async () => {
      try {
        console.log("✅ App: Calling sdk.actions.ready() as per Farcaster docs")
        await sdk.actions.ready()
        console.log("✅ App: Farcaster SDK ready() called successfully - splash screen should hide")

        // IMPORTANT: run the tripwire AFTER ready(), pass sdk for whitelisting
        try {
          assertNoExtensionProvider({ sdk });
        } catch (err) {
          // Outside Farcaster, extension present → we still fail hard
          console.warn("[tripwire] hard block:", err);
          // Optionally show a nice UI message instead of breaking the tree
          // setError("Please open this mini app inside Farcaster.");
          return;
        }

        // Isolate Farcaster wallet to prevent conflicts
        isolateFarcasterWallet();
        preventWalletConflicts();
        
        // Ensure the provider is properly configured
        const providerReady = ensureFarcasterProvider();
        if (providerReady) {
          console.log("✅ App: Farcaster wallet provider isolated and ready");
        } else {
          console.warn("⚠️ App: Farcaster wallet provider not ready");
        }
      } catch (error) {
        console.warn("❌ App: Farcaster SDK ready() failed:", error)
        console.log("This might be expected in preview mode or if SDK is not available")
      }
    })();
  }, [])

  // Probe embedded provider for diagnostics
  useEffect(() => {
    probeEmbeddedProvider();
  }, [])

  const handleTransactionSuccess = (action: string, amount: string, token: string) => {
    setSocialModalData({ action, amount, token })
    setShowSocialModal(true)
  }

  const handleTokenSelect = (token: Token) => {
    setSelectedToken(token)
  }

  // Update selectedToken when enriched tokens are loaded
  useEffect(() => {
    if (enrichedTokens.length > 0 && !selectedToken.priceInUsd) {
      setSelectedToken(enrichedTokens[0])
    }
  }, [enrichedTokens, selectedToken])

  return (
    <WalletProvider>
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-md mx-auto min-h-screen flex flex-col">
          <Header />

          <main className="flex-1 p-4 space-y-6">
            <AssetSelector 
              selectedToken={selectedToken} 
              onTokenSelect={handleTokenSelect}
              aaveData={aaveData}
              aaveLoading={aaveLoading}
            />
            <ActionTabs 
              onTransactionSuccess={handleTransactionSuccess} 
              selectedToken={selectedToken}
              aaveData={aaveData}
            />
            <Dashboard aaveData={aaveData} aaveLoading={aaveLoading} />
            <SecurityDisclaimer />
          </main>
        </div>

        {showSocialModal && socialModalData && (
          <SocialModal
            isOpen={showSocialModal}
            onClose={() => setShowSocialModal(false)}
            action={socialModalData.action}
            amount={socialModalData.amount}
            token={socialModalData.token}
          />
        )}
      </div>
    </WalletProvider>
  )
}

export default App
